import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { useGroupAdminAccess } from '~/hooks/useIsGroupAdmin';
import { AddPostalStudentForm } from '~/modules/students/AddPostalStudentForm';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students/all-students/new',
)({
  component: RouteComponent,
});

function RouteComponent(): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/students/all-students/new',
  });

  const { isGroupAdmin, isResolved } = useGroupAdminAccess();
  const { data: group } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });

  /**
   * The leaf gate. The parent (`all-students.tsx`) is a bare `<Outlet />` and
   * the only group-type test in the tree is the entry BUTTON on the index, so
   * this URL is reachable in any group the caller admins — a congregation
   * included, where the server refuses (the operation's group-type set is
   * `{STUDY_HELPER_STUDY_GROUP}` alone) and where legacy mounts a different form
   * with a different helper derivation. A hidden button is not a gate.
   *
   * `isResolved` is not optional: `getGroupBySlug` and `/users/me` are both
   * pending on first paint, and a bare `!allowed` bounces the group's own admin
   * before the payload lands.
   */
  const refused =
    isResolved &&
    group !== undefined &&
    !(isGroupAdmin && group.group_type === 'STUDY_HELPER_STUDY_GROUP');

  const handleBack = (): void => {
    navigate({
      to: '/groups/$groupName/students/all-students/',
      params: { groupName },
    });
  };

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/students/all-students/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  if (refused) return null;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('actions.add-new-postal-student')} />
      </GroupHeaderWrapper>
      <FormContainer>
        <LargeArrowBackButton onClick={handleBack} />
        {/* The form mounts only once the id EXISTS. `group_id` is
            `Path(gt=0, le=2147483647)`, so a `group?.id ?? 0` placeholder posts
            `/v3/groups/0/students` and takes a 422 the error contract carries no
            code for — on a form where no field is wrong. */}
        {group !== undefined && (
          <AddPostalStudentForm groupId={group.id} onSubmitted={handleBack} />
        )}
      </FormContainer>
    </>
  );
}
